/**
 * ⭐ The intake schedule (owner, 2026-10-22 — the Supplements pass): what is due on a day,
 * when, how much, and what the record says. ONE engine for Supplements and, later,
 * Medications ("1 yes"). Pure: no store, no clock — every function takes the date.
 *
 * The planner organises what the person entered. It never prescribes and never changes a dose
 * by itself: a "phase" is an amount the person set from a date of their choosing.
 *
 * Owner decisions this encodes:
 *  · HOW OFTEN — every day, chosen weekdays (once a week = one weekday), or every N days / weeks
 *    counted from the start date; plus a start date and an optional end date.
 *  · WHEN — the four times of day stay ("2 yes"), refined by anchors to the person's day:
 *    on waking, before / with / after a meal (with minutes), before bed, a clock time, or every
 *    N hours across the waking day. Food instructions are the anchor ("30 min before breakfast"
 *    is the empty stomach) plus an optional note.
 *  · STRENGTH and AMOUNT are separate: "2 softgels · 1,000 mg each".
 *  · SKIPPED is recorded apart from missed and never counts as taken. It rides the day's id
 *    list as `~<dose key>` (no new column), so the day log's compare-and-swap covers it.
 *  · SUPPLY is counted from the record ("6 left · runs out around Sun 25"), never decremented
 *    by a tap — two devices can't lose a count, and an undone tap gives the pill back.
 *
 * ⚠️ A dose key is the item id for its FIRST time of the day and `<id>#<n>` for the others, so
 * every list saved before this engine (one daily dose, keyed by the id) still reads correctly.
 */
import { shiftYMD } from './hydration';
import { formatTime, type TimeFormat } from './time';

export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'bedtime';
export type Meal = 'breakfast' | 'lunch' | 'dinner' | 'any';
export type MealRelation = 'before' | 'with' | 'after';

/** One time in the day a dose is taken. */
export type DoseTime =
  /** The plain four times of day — what every list saved before this engine has. */
  | { at: 'slot'; slot: TimeOfDay }
  /** On waking, optionally some minutes after. */
  | { at: 'waking'; minutes?: number }
  /** Before / with / after a meal; `minutes` is how long before or after (not used for "with"). */
  | { at: 'meal'; meal: Meal; relation: MealRelation; minutes?: number }
  /** Before bed, optionally some minutes before. */
  | { at: 'bed'; minutes?: number }
  /** A clock time, "HH:MM". */
  | { at: 'clock'; time: string };

export type Frequency =
  | { kind: 'daily' }
  /** 0 = Sunday … 6 = Saturday. Once a week is one day. */
  | { kind: 'weekdays'; days: number[] }
  /** Every N days or weeks, counted from the start date. */
  | { kind: 'interval'; every: number; unit: 'days' | 'weeks' };

export const STRENGTH_UNITS = ['mg', 'mcg', 'g', 'IU', 'ml', 'CFU', 'bn CFU', '%'] as const;
export type StrengthUnit = (typeof STRENGTH_UNITS)[number];
export const UNIT_FORMS = ['tablet', 'capsule', 'softgel', 'scoop', 'drop', 'ml', 'gummy', 'sachet'] as const;
export type UnitForm = (typeof UNIT_FORMS)[number];

/** "Change the amount from a date" — stored as phases; amount only, for now (owner "4 yes"). */
export interface AmountChange { from: string; amount: number }

/** The schedule fields an intake item may carry. All optional: a list saved before has none. */
export interface IntakeSchedule {
  /** What each unit holds: 1,000 mg (per softgel). */
  strength?: { value: number; unit: StrengthUnit } | null;
  /** The unit: softgel, tablet, scoop … */
  form?: UnitForm | null;
  /** How many units each time (default 1). */
  amount?: number | null;
  /** When in the day. Absent: the item's legacy `slot`. Ignored when `everyHours` is set. */
  times?: DoseTime[] | null;
  /** Every N hours across the waking day, from `from` ("HH:MM", default the wake time). */
  everyHours?: { hours: number; from?: string | null } | null;
  frequency?: Frequency | null;
  /** YYYY-MM-DD. Absent: always has been. */
  startDate?: string | null;
  endDate?: string | null;
  reviewDate?: string | null;
  changes?: AmountChange[] | null;
  /** Units on hand on `asOf` (before that day's doses). */
  supply?: { count: number; asOf: string } | null;
  note?: string | null;
  reason?: string | null;
}

/**
 * ⭐ One pause, as the item keeps it (polish round, owner 2026-10-24 — honest pauses), YYYY-MM-DD.
 * `from` is the day Pause was pressed: from that day on nothing is due. `to` is the day Resume
 * was pressed — the first day due again; absent while the pause lasts. `from: null` is a pause
 * from before pause dates were kept, closed by a Resume: when it began, nobody knows.
 */
export interface PauseSpan { from: string | null; to?: string | null }

/** What the engine needs from an item (a Supplement or a Medication). */
export interface IntakeItem extends IntakeSchedule {
  id: string;
  name: string;
  /** The free-text dose from before strength and amount existed ("2000 IU"). */
  dose?: string;
  slot?: TimeOfDay;
  active: boolean;
  /**
   * The pause history, oldest first (`pauseItem` / `resumeItem` keep it). Absent on a list saved
   * before it: a paused item then has no date — `pauseOn` says which days that leaves unknown.
   */
  pauses?: PauseSpan[] | null;
}

/** The person's day, "HH:MM". A setting later (step 4); these defaults until then. */
export interface DayTimes { wake: string; breakfast: string; lunch: string; dinner: string; bed: string }
export const DEFAULT_DAY_TIMES: DayTimes = { wake: '07:00', breakfast: '07:30', lunch: '13:00', dinner: '19:30', bed: '23:00' };

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_KEYS = ['wake', 'breakfast', 'lunch', 'dinner', 'bed'] as const;

/** The saved day (user_preferences.intake_day_times), each time checked; anything missing or bad → its default. */
export function readDayTimes(raw: unknown): DayTimes {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = { ...DEFAULT_DAY_TIMES };
  for (const k of DAY_KEYS) if (typeof o[k] === 'string' && HHMM.test(o[k] as string)) out[k] = o[k] as string;
  return dayTimesError(out) ? { ...DEFAULT_DAY_TIMES } : out;
}

/**
 * Why a day can't be saved, or null. The times must run in the day's order — waking, breakfast,
 * lunch, dinner, bed — within one calendar day (the compartments are cut between them).
 */
export function dayTimesError(d: DayTimes): string | null {
  for (const k of DAY_KEYS) if (!HHMM.test(d[k])) return 'Enter each time as hours and minutes.';
  const m = DAY_KEYS.map(k => toMinutes(d[k]));
  for (let i = 1; i < m.length; i++) if (m[i] <= m[i - 1]) return 'The times need to run in order: waking, breakfast, lunch, dinner, then bed.';
  return null;
}

export const TIMES_OF_DAY: { id: TimeOfDay; label: string }[] = [
  { id: 'morning', label: 'Morning' },
  { id: 'afternoon', label: 'Afternoon' },
  { id: 'evening', label: 'Evening' },
  { id: 'bedtime', label: 'Bedtime' },
];

/** One dose due on a day. */
export interface Dose {
  /** The id in the day's record: the item id for the first time, `<id>#<n>` for the others. */
  key: string;
  itemId: string;
  time: DoseTime;
  /** Minutes after midnight on the person's day — orders the list (never shown as a deadline). */
  minute: number;
  timeOfDay: TimeOfDay;
  /** Units this time (the amount change in force on the day, else the item's amount). */
  amount: number;
}

// ── Dates ────────────────────────────────────────────────────────────────────────────────

/** 0 = Sunday … 6 = Saturday, for a YYYY-MM-DD (the calendar day, whatever the time zone). */
export const weekdayOf = (ymd: string): number => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};
const daysFrom = (a: string, b: string): number => {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
};

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}
export function toHHMM(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

// ── Pauses ───────────────────────────────────────────────────────────────────────────────

/**
 * active: due as its schedule says · paused: inside a dated pause — nothing due · unknown: paused
 * before pause dates were kept — whether it was due that day nobody knows (never "not recorded",
 * never "not due").
 */
export type PauseState = 'active' | 'paused' | 'unknown';
/** A pause read back: both ends known or null (`to` null — it lasts). */
export interface PauseRead { from: string | null; to: string | null }

const YMD = /^\d{4}-\d{2}-\d{2}$/;
/** A saved day: the date, null when absent, or undefined when it isn't one (the entry is dropped). */
const readYMD = (v: unknown): string | null | undefined => (v === null || v === undefined ? null : typeof v === 'string' && YMD.test(v) ? v : undefined);
/** Where a pause starts in the list's order — an undated one at its end (it began some time before). */
const startOf = (s: PauseRead) => (s.from ?? s.to)!;

/**
 * The saved pauses, read defensively — never a throw on a malformed list: an entry without a valid
 * date is dropped, as is one that ends before it begins; oldest first; only the last may still
 * last (an earlier open one ends where the next begins).
 */
export function pauseSpans(raw: unknown): PauseRead[] {
  if (!Array.isArray(raw)) return [];
  const out: PauseRead[] = [];
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue;
    const from = readYMD((e as PauseSpan).from), to = readYMD((e as PauseSpan).to);
    if (from === undefined || to === undefined || (from === null && to === null)) continue;
    if (from !== null && to !== null && to < from) continue;
    out.push({ from, to });
  }
  out.sort((a, b) => startOf(a).localeCompare(startOf(b)));
  for (let i = 0; i < out.length - 1; i++) {
    if (out[i].to === null) out[i] = { ...out[i], to: startOf(out[i + 1]) };
  }
  return out;
}

/**
 * Was the item paused on `date`? A day inside a dated pause is paused — from the day Pause was
 * pressed up to (not including) the day Resume was. A day an undated pause covers is unknown: one
 * closed by a Resume (`from: null`) covers the days after the pause before it, up to its `to`; an
 * item paused with no open pause (a list saved before pause dates) covers every day after its last
 * pause. Any other day is active — the days before a pause are due as its schedule says.
 * ⚠️ An ACTIVE item with a pause still open was resumed by an app from before pause dates: that
 * pause is read as never having been (the days read as that app showed them — never hidden from Today).
 */
export function pauseOn(item: Pick<IntakeItem, 'active' | 'pauses'>, date: string): PauseState {
  let end: string | null = null;  // the latest day a known pause ended: an undated one began after it
  for (const s of pauseSpans(item.pauses)) {
    if (s.to === null) {
      // the pause that lasts — the last one, so every earlier one is behind this day
      if (!item.active) return date >= s.from! ? 'paused' : 'active';
      continue;
    }
    if (s.from === null ? (end === null || date >= end) && date < s.to : date >= s.from && date < s.to) return s.from === null ? 'unknown' : 'paused';
    if (end === null || s.to > end) end = s.to;
  }
  if (!item.active) return end === null || date >= end ? 'unknown' : 'active';
  return 'active';
}

/** The day the item's pause began — null while it's active, or paused with no date kept. */
export function pausedSince(item: Pick<IntakeItem, 'active' | 'pauses'>): string | null {
  if (item.active) return null;
  const spans = pauseSpans(item.pauses);
  const last = spans[spans.length - 1];
  return last && last.to === null ? last.from : null;
}

/** How long a pause is kept once it has ended — no record reaches back further (the stores read a year at most). */
const PAUSE_KEEP_DAYS = 366;
/** The pauses worth keeping on `today`: read clean, those that ended over a year ago dropped. */
const keptSpans = (item: Pick<IntakeItem, 'pauses'>, today: string): PauseRead[] => {
  const cut = shiftYMD(today, -PAUSE_KEEP_DAYS);
  return pauseSpans(item.pauses).filter(s => s.to === null || s.to >= cut);
};
const toSaved = (spans: readonly PauseRead[]): PauseSpan[] => spans.map(s => (s.to === null ? { from: s.from } : { from: s.from, to: s.to }));

/**
 * Pause the item on `today` (the day Pause is pressed — nothing is due from it on). Paused again
 * the day it was resumed, its last pause simply goes on. An already paused item is returned as is.
 */
export function pauseItem<T extends Pick<IntakeItem, 'active' | 'pauses'>>(item: T, today: string): T {
  if (!item.active) return item;
  // an open pause on an active item was resumed by an older app (`pauseOn`): it never was
  const spans = keptSpans(item, today).filter(s => s.to !== null);
  const last = spans[spans.length - 1];
  if (last && last.to! >= today) {
    spans.pop();
    // an undated pause going on again is undated again: paused, with no open pause (`pauseOn`)
    if (last.from !== null) spans.push({ from: last.from, to: null });
  } else {
    spans.push({ from: today, to: null });
  }
  return { ...item, active: false, pauses: toSaved(spans) };
}

/**
 * Resume the item on `today` — the first day due again. Resumed the day it was paused, no day was
 * paused: that pause goes. Paused before pause dates were kept, its pause ends today, from a day
 * nobody knows (`{ from: null, to: today }`). An active item is returned as is.
 */
export function resumeItem<T extends Pick<IntakeItem, 'active' | 'pauses'>>(item: T, today: string): T {
  if (item.active) return item;
  const spans = keptSpans(item, today);
  const open = spans.length && spans[spans.length - 1].to === null ? spans.pop()! : null;
  if (open) {
    if (open.from !== null && open.from < today) spans.push({ from: open.from, to: today });
  } else {
    const end = spans.reduce<string | null>((e, s) => (e === null || s.to! > e ? s.to : e), null);
    if (end === null || end < today) spans.push({ from: null, to: today });
  }
  return { ...item, active: true, pauses: toSaved(spans) };
}

/** A press read back from a pause history: Pause on `date`, or Resume on it (`undated`: of a pause nobody knows the start of). */
interface PauseEvent { date: string; kind: 'pause' | 'resume'; undated?: true }

/** One side's presses, in its own order — a paused item with no open pause had a Pause nobody dated, after the rest. */
function pauseEvents(item: Pick<IntakeItem, 'active' | 'pauses'>): PauseEvent[] {
  const out: PauseEvent[] = [];
  for (const s of pauseSpans(item.pauses)) {
    if (s.from !== null) out.push({ date: s.from, kind: 'pause' });
    if (s.to !== null) out.push({ date: s.to, kind: 'resume', ...(s.from === null ? { undated: true as const } : {}) });
  }
  const open = out.length > 0 && out[out.length - 1].kind === 'pause';
  // an ACTIVE item with a pause still open was resumed by an older app: that pause never was (`pauseOn`)
  if (item.active && open) out.pop();
  if (!item.active && !open) out.push({ date: out.length ? out[out.length - 1].date : '', kind: 'pause', undated: true });
  return out;
}

/**
 * ⭐ Two copies of one item's pause history, changed on two devices (the list's merge — lib/syncDoc
 * `{ records: 'id', of }` — sends a record changed on BOTH sides here; polish round, fix 1): the
 * presses of both, replayed in date order — a Pause while paused, or a Resume while due, changes
 * nothing — so a stale tab can neither move a pause's start nor undo a Resume it never saw. On one
 * day this tab's press comes last (it is the later write). `active` follows the merged history.
 */
export function mergePauses<T extends Pick<IntakeItem, 'active' | 'pauses'>>(local: T, cloud: Pick<IntakeItem, 'active' | 'pauses'>): T {
  const events = [...pauseEvents(cloud).map(e => ({ ...e, side: 0 })), ...pauseEvents(local).map(e => ({ ...e, side: 1 }))]
    .map((e, i) => ({ ...e, i }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.side - b.side || a.i - b.i);
  const spans: PauseRead[] = [];
  let paused = false;
  let from: string | null = null;
  for (const e of events) {
    if (e.kind === 'pause' && !paused) { paused = true; from = e.undated ? null : e.date; }
    else if (e.kind === 'resume' && paused) { if (from !== e.date) spans.push({ from, to: e.date }); paused = false; }
    // a Resume of a pause nobody dated, the other side never paused: it was paused before it — since when, nobody knows
    else if (e.kind === 'resume' && e.undated && !spans.some(s => s.to === e.date)) spans.push({ from: null, to: e.date });
  }
  if (paused && from !== null) spans.push({ from, to: null });
  return { ...local, active: !paused, pauses: toSaved(spans) };
}

/** The whole record, for the list's merge: this tab's copy (the later write), its pauses merged (`mergePauses`). */
export function mergeIntakeRecord(_base: unknown, local: unknown, cloud: unknown): unknown {
  const isItem = (v: unknown): v is IntakeItem => !!v && typeof v === 'object' && typeof (v as IntakeItem).active === 'boolean';
  return isItem(local) && isItem(cloud) ? mergePauses(local, cloud) : local;
}

// ── Is it due? ───────────────────────────────────────────────────────────────────────────

/**
 * Is the item due at all on `date`? Not paused that day (`pauseOn` — a day before its pause is due,
 * a day it was paused or a day nobody knows isn't), inside its dates, on its days.
 */
export function isDueOn(item: IntakeItem, date: string): boolean {
  return pauseOn(item, date) === 'active' && onSchedule(item, date);
}

/** Its dates and its days — paused or not. */
function onSchedule(item: IntakeItem, date: string): boolean {
  if (item.startDate && date < item.startDate) return false;
  if (item.endDate && date > item.endDate) return false;
  const f = item.frequency;
  if (!f || f.kind === 'daily') return true;
  if (f.kind === 'weekdays') return f.days.includes(weekdayOf(date));
  const step = Math.max(1, Math.floor(f.every)) * (f.unit === 'weeks' ? 7 : 1);
  // ⚠️ Counted from the start date; with none there is no day to count from — every day.
  if (!item.startDate) return true;
  return daysFrom(item.startDate, date) % step === 0;
}

/** The units each time on `date`: the latest amount change from on or before it, else the amount. */
export function amountOn(item: IntakeItem, date: string): number {
  let amount = item.amount && item.amount > 0 ? item.amount : 1;
  let from = '';
  for (const c of item.changes ?? []) {
    if (c.from <= date && c.from >= from && c.amount > 0) { amount = c.amount; from = c.from; }
  }
  return amount;
}

/** The times in the day the item is taken (legacy: its slot). */
export function timesOf(item: IntakeItem, day: DayTimes = DEFAULT_DAY_TIMES): DoseTime[] {
  const every = item.everyHours;
  if (every && every.hours > 0) {
    const start = toMinutes(every.from || day.wake);
    let end = toMinutes(day.bed);
    if (end <= start) end += 1440;
    const out: DoseTime[] = [];
    for (let m = start; m <= end && out.length < 24; m += every.hours * 60) out.push({ at: 'clock', time: toHHMM(m) });
    return out;
  }
  if (item.times && item.times.length) return item.times;
  return [{ at: 'slot', slot: item.slot ?? 'morning' }];
}

const mealTime = (meal: Meal, day: DayTimes) =>
  // "With any meal" sits at lunch — the middle of the day.
  toMinutes(meal === 'breakfast' ? day.breakfast : meal === 'dinner' ? day.dinner : day.lunch);

/** Where a time falls on the person's day, in minutes after midnight. */
export function minuteOf(t: DoseTime, day: DayTimes = DEFAULT_DAY_TIMES): number {
  switch (t.at) {
    case 'slot':
      return toMinutes(t.slot === 'morning' ? day.breakfast : t.slot === 'afternoon' ? day.lunch : t.slot === 'evening' ? day.dinner : day.bed);
    case 'waking': return toMinutes(day.wake) + (t.minutes ?? 0);
    case 'meal': {
      const at = mealTime(t.meal, day);
      return t.relation === 'before' ? at - (t.minutes ?? 0) : t.relation === 'after' ? at + (t.minutes ?? 0) : at;
    }
    case 'bed': return toMinutes(day.bed) - (t.minutes ?? 0);
    case 'clock': return toMinutes(t.time);
  }
}

/**
 * The compartment a time falls in. A slot is its own; before bed is Bedtime; anything else by
 * where it falls between the meals (the midpoints of breakfast–lunch, lunch–dinner, dinner–bed).
 */
export function timeOfDayOf(t: DoseTime, day: DayTimes = DEFAULT_DAY_TIMES): TimeOfDay {
  if (t.at === 'slot') return t.slot;
  if (t.at === 'bed') return 'bedtime';
  const m = minuteOf(t, day);
  const mid = (a: string, b: string) => (toMinutes(a) + toMinutes(b)) / 2;
  if (m < mid(day.breakfast, day.lunch)) return 'morning';
  if (m < mid(day.lunch, day.dinner)) return 'afternoon';
  if (m < mid(day.dinner, day.bed)) return 'evening';
  return 'bedtime';
}

const doseKey = (id: string, n: number) => (n === 0 ? id : `${id}#${n}`);

/** The item's doses due on `date`, in the day's order. */
export function dosesOn(item: IntakeItem, date: string, day: DayTimes = DEFAULT_DAY_TIMES): Dose[] {
  return isDueOn(item, date) ? scheduledDoses(item, date, day) : [];
}

/**
 * The doses the item's schedule places on `date` whether it was paused or not — only to place a
 * paused day's marks (what was marked during a pause still counts). What is DUE is `dosesOn`.
 */
export function scheduledDoses(item: IntakeItem, date: string, day: DayTimes = DEFAULT_DAY_TIMES): Dose[] {
  if (!onSchedule(item, date)) return [];
  const amount = amountOn(item, date);
  return timesOf(item, day).map((time, n) => ({
    key: doseKey(item.id, n),
    itemId: item.id,
    time,
    minute: minuteOf(time, day),
    timeOfDay: timeOfDayOf(time, day),
    amount,
  }));
}

/** The day's order: by the clock, then by name. */
const inDayOrder = (items: readonly IntakeItem[]) => {
  const name = new Map(items.map(i => [i.id, i.name]));
  return (a: Dose, b: Dose) => a.minute - b.minute || (name.get(a.itemId) ?? '').localeCompare(name.get(b.itemId) ?? '') || a.key.localeCompare(b.key);
};

/** Every dose due on `date` across the list, in the day's order (then by name). */
export function dayDoses(items: readonly IntakeItem[], date: string, day: DayTimes = DEFAULT_DAY_TIMES): Dose[] {
  return items.flatMap(i => dosesOn(i, date, day)).sort(inDayOrder(items));
}

/**
 * ⭐ The doses a day COUNTS — one rule for every count of a recorded day (Supplements polish round,
 * fix 1): Today's tray and list, the row's `scheduled`, the week's strip and pill box, the item
 * rows. The doses due (`dayDoses`), and a dose its item's schedule placed on a day it was PAUSED
 * that was marked all the same — taken (or skipped) before Pause was pressed that day: a dose
 * taken is a dose taken. A paused item's unmarked doses are never due. `ids` is that day's record.
 */
export function countedDoses(items: readonly IntakeItem[], date: string, ids: readonly string[], day: DayTimes = DEFAULT_DAY_TIMES): Dose[] {
  return items
    .flatMap(i => {
      const doses = scheduledDoses(i, date, day);
      return pauseOn(i, date) === 'active' ? doses : doses.filter(d => markOf(ids, d.key) !== null);
    })
    .sort(inDayOrder(items));
}

/** Does this record key belong to the item (its first dose or one of its others)? */
export const keyOfItem = (key: string, itemId: string) => key === itemId || key.startsWith(`${itemId}#`);

// ── The day's record: taken, skipped ─────────────────────────────────────────────────────

export const SKIP_PREFIX = '~';
export type DoseMark = 'taken' | 'skipped' | null;

export function markOf(ids: readonly string[], key: string): DoseMark {
  if (ids.includes(key)) return 'taken';
  if (ids.includes(SKIP_PREFIX + key)) return 'skipped';
  return null;
}

/** Set one dose's mark — the same array back when nothing changes (so nothing is written). */
export function withMark(ids: readonly string[], key: string, mark: DoseMark): string[] {
  if (markOf(ids, key) === mark) return ids as string[];
  const rest = ids.filter(x => x !== key && x !== SKIP_PREFIX + key);
  if (mark === 'taken') rest.push(key);
  if (mark === 'skipped') rest.push(SKIP_PREFIX + key);
  return rest;
}

/**
 * A ONE-OFF dose (owner: "extras all yes") — taken on a day it wasn't due, or once more than
 * due — is `<id>#+<n>` in the same list. It counts for supply, never for adherence (it isn't
 * a due dose), so a day can't read "4 of 3".
 */
export const EXTRA_MARK = '#+';
export const isExtraKey = (key: string) => key.includes(EXTRA_MARK);
/** The next free one-off key for the item in a day's record. */
export function extraKey(ids: readonly string[], itemId: string): string {
  let n = 1;
  while (ids.includes(`${itemId}${EXTRA_MARK}${n}`)) n++;
  return `${itemId}${EXTRA_MARK}${n}`;
}

/** Due doses taken in a day's record (a skip is never one; neither is a one-off). */
export const takenCount = (ids: readonly string[] | null | undefined) =>
  (ids ?? []).filter(x => !x.startsWith(SKIP_PREFIX) && !isExtraKey(x)).length;
export const skippedCount = (ids: readonly string[] | null | undefined) => (ids ?? []).filter(x => x.startsWith(SKIP_PREFIX)).length;

// ── Words ────────────────────────────────────────────────────────────────────────────────

const plural = (n: number, word: string) => (n === 1 ? word : word === 'ml' ? 'ml' : `${word}s`);
const num = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

/**
 * A clock time in the app's 12/24-hour setting (`progressDisplayStore.timeFormat`, passed in —
 * the engine reads no store): "7:00 PM" or "19:00". Takes "HH:MM" or minutes after midnight.
 * ⚠️ `fmt` has no default (nor do the helpers below): every caller says which it shows.
 */
export function clockText(at: string | number, fmt: TimeFormat): string {
  return formatTime(typeof at === 'number' ? toHHMM(at) : at, fmt);
}

/** "7:00–7:30 AM" (one AM/PM when both share it), "11:30 AM–1:00 PM", "07:00–07:30"; one time when they're equal. */
export function clockRange(a: string | number, b: string | number, fmt: TimeFormat): string {
  const from = clockText(a, fmt), to = clockText(b, fmt);
  if (from === to) return from;
  if (fmt === '12h' && from.slice(-2) === to.slice(-2)) return `${from.slice(0, -3)}–${to}`;
  return `${from}–${to}`;
}

/** "On waking", "30 min before breakfast", "With lunch", "Before bed", "At 7:00 PM", "Morning". */
export function timeLabel(t: DoseTime, fmt: TimeFormat): string {
  switch (t.at) {
    case 'slot': return TIMES_OF_DAY.find(s => s.id === t.slot)?.label ?? 'Morning';
    case 'waking': return t.minutes ? `${t.minutes} min after waking` : 'On waking';
    case 'meal': {
      const meal = t.meal === 'any' ? 'any meal' : t.meal;
      if (t.relation === 'with') return `With ${meal}`;
      if (t.minutes) return `${t.minutes} min ${t.relation} ${meal}`;
      return `${t.relation === 'before' ? 'Before' : 'After'} ${meal}`;
    }
    case 'bed': return t.minutes ? `${t.minutes} min before bed` : 'Before bed';
    case 'clock': return `At ${clockText(t.time, fmt)}`;
  }
}

/**
 * The time inside a running line ("Probiotic 7:00 AM · Vitamin D3 with breakfast",
 * "1 scoop · with any meal"): a clock time is its clock, an anchor is lower case, and a plain
 * time of day is nothing — the line's own time of day already says it.
 */
export function timePhrase(t: DoseTime, fmt: TimeFormat): string {
  if (t.at === 'clock') return clockText(t.time, fmt);
  if (t.at === 'slot') return '';
  const label = timeLabel(t, fmt);
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/**
 * "2 softgels · 1,000 mg each" — amount and strength, both always when known. An item from
 * before strength existed shows its own dose text ("2000 IU").
 */
export function doseText(item: IntakeItem, amount: number = item.amount && item.amount > 0 ? item.amount : 1): string {
  const form = item.form ?? null;
  const s = item.strength;
  const units = form ? `${num(amount)} ${plural(amount, form)}` : null;
  const each = s ? `${num(s.value)} ${s.unit}${units ? ' each' : ''}` : null;
  if (units || each) return [units, each].filter(Boolean).join(' · ');
  return item.dose?.trim() ?? '';
}

/** "Sun" … "Sat", by `weekdayOf` — the one short weekday table (Supplements' dates: supplementsFormat). */
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const WEEKDAY_PLURAL = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

/** "Every day", "Mon · Wed · Fri", "Thursdays", "Every 2 days", "Every 3 weeks". */
export function frequencyLabel(f: Frequency | null | undefined): string {
  if (!f || f.kind === 'daily') return 'Every day';
  if (f.kind === 'weekdays') {
    const days = [...f.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)); // Monday first
    if (days.length === 1) return WEEKDAY_PLURAL[days[0]];
    if (days.length === 7) return 'Every day';
    return days.map(d => WEEKDAY_SHORT[d]).join(' · ');
  }
  const n = Math.max(1, Math.floor(f.every));
  if (n === 1) return f.unit === 'weeks' ? 'Every week' : 'Every day';
  return `Every ${n} ${f.unit}`;
}

/** Due every day (a frequency that skips no day). */
export function isDaily(f: Frequency | null | undefined): boolean {
  if (!f || f.kind === 'daily') return true;
  if (f.kind === 'weekdays') return new Set(f.days).size >= 7;
  return f.unit === 'days' && Math.max(1, Math.floor(f.every)) === 1;
}

/**
 * How much, how often, in units — the supply's rate: "2 a day", "1 every 2 days",
 * "1 on Thursdays", "2 on Mon · Wed · Fri", "1 a week", "1 every 3 weeks". The units on a day
 * it's due (every time that day × the amount in force on `date`), so it reads the same on a day
 * the item isn't due — never "None due today".
 */
export function rateText(item: IntakeItem, date: string, day: DayTimes = DEFAULT_DAY_TIMES): string {
  const n = num(timesOf(item, day).length * amountOn(item, date));
  const f = item.frequency;
  if (!f || f.kind === 'daily' || isDaily(f)) return `${n} a day`;
  if (f.kind === 'weekdays') return `${n} on ${frequencyLabel(f)}`;
  const every = Math.max(1, Math.floor(f.every));
  if (every === 1) return `${n} a week`; // every 1 day is daily, above
  return `${n} every ${every} ${f.unit}`;
}

/** The first day on or after `from` the item is due (a year ahead at most), or null. */
export function nextDueOn(item: IntakeItem, from: string): string | null {
  for (let i = 0; i <= 366; i++) {
    const date = shiftYMD(from, i);
    if (isDueOn(item, date)) return date;
  }
  return null;
}

// ── Supply ───────────────────────────────────────────────────────────────────────────────

/** A day's record, as the stores keep it. */
export interface IntakeDay { date: string; takenIds: string[] }

/**
 * Units left: the count on `asOf`, less every unit the record shows taken since (each taken dose
 * at the amount in force that day). `null` when there is no count, or the record doesn't reach
 * back to `asOf` (`recordFrom`, the earliest loaded day) — never a guess.
 */
export function supplyLeft(item: IntakeItem, days: readonly IntakeDay[], recordFrom: string): number | null {
  const s = item.supply;
  if (!s) return null;
  if (s.asOf < recordFrom) return null;
  let left = s.count;
  for (const d of days) {
    if (d.date < s.asOf) continue;
    const taken = d.takenIds.filter(k => !k.startsWith(SKIP_PREFIX) && keyOfItem(k, item.id)).length;
    left -= taken * amountOn(item, d.date);
  }
  return Math.max(0, left);
}

/**
 * The first day a due dose can't be covered by what's left, counting today's doses not yet
 * taken (`takenToday`: today's record). `null` with no count, or nothing due within a year.
 */
export function runsOutOn(
  item: IntakeItem,
  left: number | null,
  today: string,
  takenToday: readonly string[],
  day: DayTimes = DEFAULT_DAY_TIMES,
): string | null {
  if (left === null) return null;
  let rest = left;
  for (let i = 0; i <= 366; i++) {
    const date = shiftYMD(today, i);
    for (const d of dosesOn(item, date, day)) {
      if (i === 0 && markOf(takenToday, d.key) !== null) continue; // taken (already counted) or skipped
      if (rest < d.amount) return date;
      rest -= d.amount;
    }
  }
  return null;
}

/** "Running low": it runs out within this many days. */
export const RUNNING_LOW_DAYS = 7;
export function isRunningLow(runsOut: string | null, today: string): boolean {
  return runsOut !== null && daysFrom(today, runsOut) <= RUNNING_LOW_DAYS;
}

/** The next amount change after `today`, if any ("2 softgels → 1 softgel from Nov 1"). */
export function nextChange(item: IntakeItem, today: string): AmountChange | null {
  const ahead = (item.changes ?? []).filter(c => c.from > today && c.amount > 0).sort((a, b) => a.from.localeCompare(b.from));
  return ahead[0] ?? null;
}

/** Due for a review (the date has come). A quiet notice — nothing stops. */
export const reviewDue = (item: IntakeItem, today: string) => !!item.reviewDate && item.reviewDate <= today;

/** Days from `a` to `b` (calendar). */
export const daysBetween = daysFrom;
